import { useBookingStore } from '../../Registration/stores/bookingStore';
import { useSettingsManager } from '../../Registration/stores/settingsManagerStore';
import {dataGet} from '../../Common/utils';
import { useI18n } from 'vue-i18n';
export function useFieldLimits() {
    const bookingStore = useBookingStore();
    const { fieldLimits } = useSettingsManager();
    const { t } = useI18n();

    const getFieldOptionAvailabilityText = (limitsEnabled, fieldCoreId, fieldFormMapping, optionKey, optionCoreId, prefix = '') => {
        if (!limitsEnabled) {
            return '';
        }

        const availability = getFieldOptionAvailability(fieldCoreId, fieldFormMapping, optionKey, optionCoreId);
        if (availability === null) {
            return '';
        }

        const shouldDisable = shouldDisableFieldOption(fieldFormMapping, optionKey, optionCoreId, prefix, fieldCoreId);
        if (shouldDisable) {
            return `(${t('live.limits.unavailable.select')})`;
        }

        return `(${t('live.limits.select.options.availability', { num: availability })})`;
    };

    const getFieldOptionAvailability = (fieldCoreId, fieldFormMapping, optionKey, optionCoreId) => {
        const extendedFields = fieldLimits?.extended_fields || [];
        const coreField = extendedFields.find(field => field.id === fieldCoreId);

        if (!coreField) {
            return null;
        }

        const coreFieldOptions = coreField.options || [];
        const currentCoreFieldOption = coreFieldOptions.find(option => option.id === optionCoreId);
        const limit = currentCoreFieldOption?.limit;

        // If there's a limit validate
        if (limit !== null && limit !== undefined) {
            const totalUsage = getCurrentFieldOptionTotalUsage(fieldFormMapping, optionKey, fieldCoreId);
            return limit - totalUsage;
        }

        return null;
    };

    const getCurrentFieldOptionTotalUsage = (formMapping, optionKey, fieldCoreId) => {
        const currentUsage = fieldLimits?.current_usage || {};
        const fieldAPIUsage = currentUsage[fieldCoreId] || {};

        const currentLocalUsage = getFieldOptionLocalUsage(optionKey, formMapping);
        const initialLocalUsage = getFieldOptionInitialLocalUsage(optionKey, formMapping);
        const currentOptionCoreUsage = parseInt(fieldAPIUsage[optionKey] || 0);

        // Calculate the total usage of the field option by combining the API-reported usage,
        // subtracting the initial local usage (to avoid double-counting), and adding the current local changes.
        return (currentOptionCoreUsage - initialLocalUsage) + currentLocalUsage;
    };

    const getFieldOptionLocalUsage = (optionKey, formMapping) => {
        let usage = 0;
        const mainGuestCurrentValue = dataGet(bookingStore.data, formMapping, null);

        if (!Array.isArray(mainGuestCurrentValue)) {
            if (mainGuestCurrentValue === optionKey) {
                usage++;
            }
        } else if (mainGuestCurrentValue.includes(optionKey)) {
            usage++;
        }

        const companions = dataGet(bookingStore.data, 'companions', []);
        companions.forEach(companion => {
            const currentCompanionOptionValue = dataGet(companion, formMapping, null);
            if (!Array.isArray(currentCompanionOptionValue)) {
                if (currentCompanionOptionValue === optionKey) {
                    usage++;
                }
            } else if (currentCompanionOptionValue.includes(optionKey)) {
                usage++;
            }
        });

        return usage;
    };

    const getFieldOptionInitialLocalUsage = (optionKey, formMapping) => {
        let usage = 0;
        const initialData = bookingStore.initialBookingData?.data;

        if (!initialData) {
            return usage;
        }

        const mainGuestCurrentValue = dataGet(initialData, formMapping, null);
        if (!Array.isArray(mainGuestCurrentValue)) {
            if (mainGuestCurrentValue === optionKey) {
                usage++;
            }
        } else if (mainGuestCurrentValue.includes(optionKey)) {
            usage++;
        }

        const companions = dataGet(initialData, 'companions', []);
        companions.forEach(companion => {
            const currentCompanionOptionValue = dataGet(companion, formMapping, []);
            if (!Array.isArray(currentCompanionOptionValue)) {
                if (currentCompanionOptionValue === optionKey) {
                    usage++;
                }
            } else if (currentCompanionOptionValue.includes(optionKey)) {
                usage++;
            }
        });

        return usage;
    };

    const shouldDisableFieldOption = (fieldFormMapping, optionKey, optionCoreId = null, prefix = '', fieldCoreId = null) => {
        // Get initial data if exists
        const value = getFieldCurrentValue(fieldFormMapping, prefix);
        if (Array.isArray(value)) {
            if (value.includes(optionKey)) {
                return false;
            }
        } else if (value === optionKey) {
            return false;
        }

        // If there's no initial data disable option if it's not available
        return !isFieldOptionAvailable(fieldFormMapping, optionKey, prefix, fieldCoreId, optionCoreId);
    };

    const getFieldCurrentValue = (formMapping, prefix = '') => {
        return getFieldValueFromProp(bookingStore.data, formMapping, prefix);
    };

    const getFieldValueFromProp = (data, formMapping, prefix = '') => {
        let value = null;
        if (prefix !== '') { // if a prefix is added it means it belongs to a companion
            // Extract companion key from prefix
            const companionKey = prefix.replace('companions.', '').replace('.', '');
            const currentCompanion = bookingStore.data.companions?.find(c => c.uniqueId === companionKey);

            if (currentCompanion !== undefined) {
                const companionCoreId = currentCompanion.id;
                if (companionCoreId !== null && companionCoreId !== undefined) {
                    const initialCompanionData = data?.companions?.find(c => c.id === companionCoreId);
                    value = dataGet(initialCompanionData, formMapping, null);
                } else {
                    const companionFromData = data?.companions?.find(c => c.uniqueId === companionKey);
                    value = dataGet(companionFromData, formMapping, null);
                }
            }
        } else {
            value = dataGet(data, formMapping, null);
        }

        return value;
    };

    const isFieldOptionAvailable = (fieldFormMapping, optionKey, prefix = null, fieldCoreId = null, optionCoreId = null) => {
        const extendedFields = fieldLimits?.extended_fields || [];
        const coreField = extendedFields.find(field => field.id === fieldCoreId);

        if (coreField && dataGet(coreField, 'limits.enabled', false) === true) {
            const coreFieldOptions = coreField.options || [];
            const currentCoreFieldOption = coreFieldOptions.find(option => option.id === optionCoreId);
            const limit = currentCoreFieldOption?.limit;

            // If there's a limit validate
            if (limit !== null && limit !== undefined) {
                const totalUsage = getCurrentFieldOptionTotalUsage(fieldFormMapping, optionKey, fieldCoreId);
                return (limit - totalUsage) > 0;
            }
        }

        return true;
    };

    const getFieldInitialValue = (formMapping, prefix = '') => {
        return getFieldValueFromProp(bookingStore.initialBookingData?.data, formMapping, prefix);
    };

    const isFormItemReadOnly = (isReadOnly, formMapping, prefix = '') => {
        if (!formMapping) {
            return false;
        }

        // Only make it read only if it already has an initial value
        if (isReadOnly) {
            return Boolean(getFieldInitialValue(formMapping, prefix));
        }

        return false;
    };

    return {
        getFieldOptionAvailabilityText,
        getFieldOptionAvailability,
        getCurrentFieldOptionTotalUsage,
        getFieldOptionLocalUsage,
        getFieldOptionInitialLocalUsage,
        shouldDisableFieldOption,
        getFieldCurrentValue,
        getFieldValueFromProp,
        isFieldOptionAvailable,
        getFieldInitialValue,
        isFormItemReadOnly
    };
}
